/* ==========================================================================
   PITS Autopartes — JavaScript del tema
   Sin dependencias externas. Todo se registra como custom elements para que
   las secciones sigan funcionando al recargarse desde el editor de temas.
   ========================================================================== */

(function () {
  'use strict';

  const PITS = window.PITS || {};
  const routes = PITS.routes || {};

  /* ---------------------------------------------------------------- utils */

  // Replica el formato de moneda de la tienda (shop.money_format).
  function formatMoney(cents) {
    const format = PITS.moneyFormat || '${{amount}}';
    const token = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';

    const noDecimals = token.indexOf('no_decimals') !== -1;
    const commaDecimal = token.indexOf('comma_separator') !== -1;
    const apostrophe = token.indexOf('apostrophe_separator') !== -1;

    const thousands = commaDecimal ? '.' : apostrophe ? "'" : ',';
    const decimalMark = commaDecimal ? ',' : '.';

    const amount = (cents / 100).toFixed(noDecimals ? 0 : 2);
    const parts = amount.split('.');
    const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const value = parts[1] ? integer + decimalMark + parts[1] : integer;

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  function trapFocus(container) {
    const focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return function () {};
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    first.focus();
    return function () {
      container.removeEventListener('keydown', onKeydown);
    };
  }

  function debounce(fn, wait) {
    let timer;
    return function () {
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getOverlay() {
    return document.querySelector('[data-overlay]');
  }

  function lockScroll(locked) {
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  /* ------------------------------------------------------------ cart core */

  const Cart = {
    listeners: [],

    subscribe(fn) {
      this.listeners.push(fn);
    },

    publish(state) {
      this.listeners.forEach((fn) => fn(state));
      document.dispatchEvent(new CustomEvent('pits:cart:updated', { detail: state }));
    },

    sectionsToRender() {
      const ids = [];
      document.querySelectorAll('[data-cart-section]').forEach((el) => {
        const id = el.dataset.sectionId;
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids;
    },

    async request(url, body) {
      const sections = this.sectionsToRender();
      const payload = Object.assign({}, body);
      // Solo pedimos secciones si hay alguna en la página; enviar el
      // parámetro vacío hace que Shopify responda con un error.
      if (sections.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        const message = data.description || data.message || PITS.strings.cartError;
        throw new Error(message);
      }
      return data;
    },

    async add(formData) {
      const sections = this.sectionsToRender();
      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const response = await fetch(routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || PITS.strings.cartError);
      }
      this.renderSections(data.sections);
      await this.refreshCount();
      this.publish(data);
      return data;
    },

    async change(body) {
      const data = await this.request(routes.cartChange, body);
      this.renderSections(data.sections);
      this.updateCount(data.item_count);
      this.publish(data);
      return data;
    },

    async update(body) {
      const data = await this.request(routes.cartUpdate, body);
      this.renderSections(data.sections);
      this.updateCount(data.item_count);
      this.publish(data);
      return data;
    },

    async refreshCount() {
      const response = await fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      this.updateCount(cart.item_count);
      return cart;
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    renderSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (typeof html !== 'string') return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        document.querySelectorAll('[data-cart-section][data-section-id="' + id + '"]').forEach((target) => {
          const source = parsed.querySelector('[data-cart-section][data-section-id="' + id + '"]');
          if (source) target.innerHTML = source.innerHTML;
        });
      });
    },
  };

  window.PITSCart = Cart;

  /* -------------------------------------------------------- cart drawer */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-drawer]');
      this.releaseFocus = null;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      });

      const overlay = getOverlay();
      if (overlay) overlay.addEventListener('click', () => this.isOpen && this.close());

      document.addEventListener('pits:cart:added', () => this.open());
    }

    get isOpen() {
      return this.drawer && this.drawer.classList.contains('is-open');
    }

    open() {
      if (!this.drawer) return;
      const overlay = getOverlay();
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(() => overlay.classList.add('is-active'));
      }
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      this.releaseFocus = trapFocus(this.drawer);
    }

    close() {
      if (!this.drawer) return;
      const overlay = getOverlay();
      if (overlay) {
        overlay.classList.remove('is-active');
        setTimeout(() => {
          overlay.hidden = true;
        }, 200);
      }
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (this.releaseFocus) this.releaseFocus();
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-open]');
    if (!trigger) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    drawer.open();
  });

  /* -------------------------------------------------------- mobile menu */

  class MobileMenu extends HTMLElement {
    connectedCallback() {
      // El encabezado fijo usa position:sticky con z-index, lo que crea un
      // contexto de apilamiento. Dentro de él, el z-index del panel deja de
      // competir a nivel de pagina y la capa gris lo tapa. Lo movemos al
      // body para que quede fuera de esa jerarquia.
      if (this.parentElement !== document.body) {
        // Al recargar la seccion desde el editor se crea otro <mobile-menu>;
        // eliminamos el que ya habiamos trasladado para no duplicarlo.
        document.querySelectorAll('mobile-menu[data-portaled]').forEach((el) => {
          if (el !== this) el.remove();
        });
        this.setAttribute('data-portaled', '');
        document.body.appendChild(this);
        return; // connectedCallback se vuelve a ejecutar tras el traslado
      }

      this.panel = this.querySelector('[data-drawer]');

      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-menu-open]')) {
          event.preventDefault();
          this.open();
        }
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      });

      const overlay = getOverlay();
      if (overlay) overlay.addEventListener('click', () => this.isOpen && this.close());
    }

    get isOpen() {
      return this.panel && this.panel.classList.contains('is-open');
    }

    open() {
      if (!this.panel) return;
      const overlay = getOverlay();
      if (overlay) {
        overlay.hidden = false;
        requestAnimationFrame(() => overlay.classList.add('is-active'));
      }
      this.panel.classList.add('is-open');
      lockScroll(true);
      this.releaseFocus = trapFocus(this.panel);
    }

    close() {
      if (!this.panel) return;
      const overlay = getOverlay();
      if (overlay) {
        overlay.classList.remove('is-active');
        setTimeout(() => {
          overlay.hidden = true;
        }, 200);
      }
      this.panel.classList.remove('is-open');
      lockScroll(false);
      if (this.releaseFocus) this.releaseFocus();
    }
  }

  customElements.define('mobile-menu', MobileMenu);

  /* --------------------------------------------------------- hero carousel */

  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.slides = Array.from(this.querySelectorAll('.hero__slide'));
      if (!this.track || this.slides.length <= 1) return;

      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.index = 0;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.canAutoplay = this.dataset.autoplay === 'true' && !reduceMotion;

      // Activa la transición tras el primer render para no animar al cargar.
      requestAnimationFrame(() => this.track.classList.add('is-ready'));

      const prev = this.querySelector('[data-prev]');
      const next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1, true));
      if (next) next.addEventListener('click', () => this.step(1, true));

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.go(parseInt(dot.dataset.dot, 10), true));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1, true);
        if (event.key === 'ArrowRight') this.step(1, true);
      });

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.start();
      });

      this.setupSwipe();

      // Soporte del editor de temas: al seleccionar una diapositiva, saltar a ella.
      document.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target);
        if (i !== -1) {
          this.go(i, false);
          this.stop();
        }
      });
      document.addEventListener('shopify:block:deselect', () => this.start());

      this.go(0, false);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    go(i, userInitiated) {
      const total = this.slides.length;
      this.index = (i + total) % total;
      this.track.style.transform = 'translateX(' + -this.index * 100 + '%)';

      this.slides.forEach((slide, s) => {
        slide.setAttribute('aria-hidden', s === this.index ? 'false' : 'true');
      });
      this.dots.forEach((dot, d) => {
        dot.classList.toggle('is-active', d === this.index);
        dot.setAttribute('aria-selected', d === this.index ? 'true' : 'false');
      });

      // Un gesto del usuario reinicia el temporizador.
      if (userInitiated) this.start();
    }

    step(dir, userInitiated) {
      this.go(this.index + dir, userInitiated);
    }

    start() {
      this.stop();
      if (!this.canAutoplay) return;
      this.timer = setInterval(() => this.step(1, false), this.interval);
    }

    stop() {
      if (this.timer) clearInterval(this.timer);
      this.timer = null;
    }

    setupSwipe() {
      let startX = null;
      const viewport = this.querySelector('.hero-carousel__viewport') || this;

      viewport.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          startX = event.clientX;
        },
        { passive: true }
      );

      viewport.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const delta = event.clientX - startX;
        startX = null;
        if (Math.abs(delta) > 45) this.step(delta < 0 ? 1 : -1, true);
      });

      viewport.addEventListener('pointercancel', () => {
        startX = null;
      });
    }
  }

  customElements.define('hero-carousel', HeroCarousel);

  /* ----------------------------------------------------- quantity input */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min || '0', 10);
          const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + step);
          if (next === parseInt(this.input.value, 10)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* ------------------------------------------------------- product form */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      if (this.dataset.ajax === 'false') return;
      event.preventDefault();
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.setLoading(true);
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        await Cart.add(new FormData(this.form));
        document.dispatchEvent(new CustomEvent('pits:cart:added'));
        if (PITS.cartType !== 'drawer') {
          window.location.href = routes.cart;
        }
      } catch (error) {
        if (this.errorEl) {
          this.errorEl.textContent = error.message;
          this.errorEl.hidden = false;
        } else {
          alert(error.message);
        }
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      this.classList.toggle('loading', loading);
      if (this.submitButton) this.submitButton.disabled = loading;
    }
  }

  customElements.define('product-form', ProductForm);

  /* --------------------------------------------------- variant selector */

  class VariantSelector extends HTMLElement {
    connectedCallback() {
      this.variants = this.getVariantData();
      if (!this.variants) return;

      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl !== 'false';

      this.addEventListener('change', this.onChange.bind(this));
    }

    getVariantData() {
      const script = this.querySelector('[type="application/json"]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map((el) => el.value);
    }

    onChange() {
      const selected = this.selectedOptions;
      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, index) => option === selected[index])
      );

      this.updateAvailability(selected);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateInput(variant);
      this.updatePrice(variant);
      this.updateMedia(variant);
      this.updateButton(variant);
      this.updateSku(variant);
      if (this.updateUrl && this.productUrl) {
        window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
      }
    }

    updateAvailability(selected) {
      // Marca como no disponibles las combinaciones que no existen en stock.
      const groups = this.querySelectorAll('[data-option-index]');
      groups.forEach((group) => {
        const index = parseInt(group.dataset.optionIndex, 10);
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidateOptions = selected.slice();
          candidateOptions[index] = input.value;
          const match = this.variants.find((variant) =>
            variant.options.every((option, i) => (i === index ? option === input.value : option === candidateOptions[i]))
          );
          input.disabled = !match || !match.available;
        });
      });
    }

    updateInput(variant) {
      const input = document.querySelector('[data-variant-id="' + this.sectionId + '"]');
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    updatePrice(variant) {
      const priceEl = document.querySelector('[data-product-price="' + this.sectionId + '"]');
      if (!priceEl) return;
      let html = '<span>' + formatMoney(variant.price) + '</span>';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html += '<s class="price__compare">' + formatMoney(variant.compare_at_price) + '</s>';
      }
      priceEl.innerHTML = html;
    }

    updateSku(variant) {
      const skuEl = document.querySelector('[data-product-sku="' + this.sectionId + '"]');
      if (skuEl) skuEl.textContent = variant.sku || '—';

      const stockEl = document.querySelector('[data-product-stock="' + this.sectionId + '"]');
      if (stockEl) {
        stockEl.textContent = variant.available ? 'En stock' : 'Sin stock';
        stockEl.classList.toggle('product__stock--out', !variant.available);
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.querySelector('[data-gallery="' + this.sectionId + '"]');
      if (!gallery) return;
      const thumb = gallery.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
      if (thumb) thumb.click();
    }

    updateButton(variant) {
      const button = document.querySelector('[data-add-button="' + this.sectionId + '"]');
      if (!button) return;
      if (variant.available) {
        button.removeAttribute('aria-disabled');
        button.disabled = false;
        button.textContent = PITS.strings.addToCart;
      } else {
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        button.textContent = PITS.strings.soldOut;
      }
    }

    setUnavailable() {
      const button = document.querySelector('[data-add-button="' + this.sectionId + '"]');
      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        button.textContent = PITS.strings.unavailable;
      }
    }
  }

  customElements.define('variant-selector', VariantSelector);

  /* ------------------------------------------------------ product media */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.querySelectorAll('[data-media-id]').forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(thumb);
        });
      });
    }

    select(thumb) {
      if (!this.main) return;
      const full = thumb.dataset.fullSrc;
      const alt = thumb.dataset.alt || '';
      if (full) {
        this.main.innerHTML = '<img src="' + full + '" alt="' + alt.replace(/"/g, '&quot;') + '" width="900" height="900">';
      }
      this.querySelectorAll('[data-media-id]').forEach((el) => el.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
    }
  }

  customElements.define('product-gallery', ProductGallery);

  /* -------------------------------------------------------------- tabs */

  class TabGroup extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const dir = event.key === 'ArrowRight' ? 1 : -1;
          const next = (index + dir + this.tabs.length) % this.tabs.length;
          this.tabs[next].focus();
          this.select(next);
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
        tab.setAttribute('tabindex', i === index ? '0' : '-1');
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }
  }

  customElements.define('tab-group', TabGroup);

  /* ---------------------------------------------- recomendaciones */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.load(url);
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }

    async load(url) {
      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const source = parsed.querySelector('product-recommendations');
        if (source && source.innerHTML.trim()) this.innerHTML = source.innerHTML;
      } catch (error) {
        /* si falla, la sección simplemente queda vacía */
      }
    }
  }

  customElements.define('product-recommendations', ProductRecommendations);

  /* -------------------------------------------------- predictive search */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"], input[name="q"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results || !routes.predictiveSearch) return;

      this.input.addEventListener('input', debounce(this.onInput.bind(this), 250));
      this.input.addEventListener('focus', () => {
        if (this.results.innerHTML.trim()) this.results.hidden = false;
      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.results.hidden = true;
      });

      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.results.hidden = true;
      });
    }

    async onInput() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.results.hidden = true;
        return;
      }

      try {
        const url =
          routes.predictiveSearch +
          '?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product&resources[limit]=6&section_id=predictive-search';
        const response = await fetch(url);
        if (!response.ok) throw new Error('search failed');
        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const content = parsed.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.innerHTML : '';
        this.results.hidden = !this.results.innerHTML.trim();
      } catch (error) {
        this.results.hidden = true;
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------ facets */

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.form.addEventListener('change', (event) => {
        if (event.target.matches('input[type="checkbox"], select')) this.submit();
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });

      const toggle = document.querySelector('[data-facets-toggle]');
      if (toggle) {
        toggle.addEventListener('click', () => this.classList.toggle('is-open'));
      }
    }

    submit() {
      const params = new URLSearchParams(new FormData(this.form));
      // Elimina los parámetros vacíos para mantener la URL limpia.
      Array.from(params.keys()).forEach((key) => {
        if (!params.get(key)) params.delete(key);
      });
      window.location.search = params.toString();
    }
  }

  customElements.define('facet-filters', FacetFilters);

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-sort-select]');
    if (!sort) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* -------------------------------------------------------- cart items */

  document.addEventListener('change', async (event) => {
    const input = event.target.closest('[data-cart-quantity]');
    if (!input) return;
    const line = input.dataset.line;
    const quantity = parseInt(input.value, 10);
    try {
      await Cart.change({ line: parseInt(line, 10), quantity: quantity });
    } catch (error) {
      alert(error.message);
    }
  });

  document.addEventListener('click', async (event) => {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    try {
      await Cart.change({ line: parseInt(remove.dataset.line, 10), quantity: 0 });
    } catch (error) {
      alert(error.message);
    }
  });

  /* -------------------------------------------- documento tributario */

  function cleanRut(rut) {
    return (rut || '').replace(/[^0-9kK]/g, '').toUpperCase();
  }

  function formatRut(rut) {
    const clean = cleanRut(rut);
    if (clean.length < 2) return clean;
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    return body.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + '-' + dv;
  }

  function validateRut(rut) {
    const clean = cleanRut(rut);
    if (clean.length < 8) return false;
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    let sum = 0;
    let multiplier = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i], 10) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    const remainder = 11 - (sum % 11);
    const expected = remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder);
    return dv === expected;
  }

  window.PITSValidateRut = validateRut;

  class InvoiceFields extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-invoice-panel]');
      this.inputs = this.querySelectorAll('[data-invoice-input]');

      // El disparador es el atributo data-shows-invoice, no el valor literal
      // del radio: así el valor puede ser el que exija Relbase sin afectar
      // el mostrado/ocultado de los campos de factura.
      this.docRadios = this.querySelectorAll('input[name="attributes[billing_documento]"]');
      this.docRadios.forEach((radio) => {
        radio.addEventListener('change', () => this.toggle(radio.hasAttribute('data-shows-invoice')));
      });

      const checked = this.querySelector('input[name="attributes[billing_documento]"]:checked');
      this.toggle(checked ? checked.hasAttribute('data-shows-invoice') : false);

      this.querySelectorAll('[data-rut-input]').forEach((input) => {
        input.addEventListener('blur', () => {
          if (!input.value.trim()) {
            this.setError(input, false);
            return;
          }
          input.value = formatRut(input.value);
          this.setError(input, !validateRut(input.value));
        });
      });

      const form = this.closest('form');
      if (form) {
        form.addEventListener('submit', (event) => {
          if (!this.isInvoice) return;
          let invalid = false;
          this.querySelectorAll('[data-rut-input]').forEach((input) => {
            if (!validateRut(input.value)) {
              this.setError(input, true);
              invalid = true;
            }
          });
          if (invalid) {
            event.preventDefault();
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    }

    get isInvoice() {
      const checked = this.querySelector('input[name="attributes[billing_documento]"]:checked');
      return checked && checked.hasAttribute('data-shows-invoice');
    }

    setError(input, hasError) {
      const message = input.parentElement.querySelector('[data-rut-error]');
      if (message) message.hidden = !hasError;
      input.style.borderColor = hasError ? 'var(--primary)' : '';
    }

    toggle(show) {
      if (this.panel) this.panel.hidden = !show;
      this.inputs.forEach((input) => {
        input.required = show && input.dataset.invoiceInput === 'required';
        // Solo se limpian los campos del panel de factura (razón social,
        // giro). El RUT vive fuera del panel y se conserva.
        const inPanel = this.panel && this.panel.contains(input);
        if (!show && inPanel) input.value = '';
      });
    }
  }

  customElements.define('invoice-fields', InvoiceFields);

  /* --------------------------------------------- nota y atributos del carrito */

  // Persistimos nota y atributos (documento tributario) a medida que se
  // escriben. Así no se pierden cuando un cambio de cantidad vuelve a
  // renderizar la sección del carrito, y sobreviven a una recarga.
  async function persistCart(payload) {
    try {
      await fetch(routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      /* silencioso: los datos se reenvían igualmente al enviar el formulario */
    }
  }

  const persistNote = debounce((value) => persistCart({ note: value }), 500);
  const persistAttribute = debounce((name, value) => {
    const attributes = {};
    attributes[name] = value;
    persistCart({ attributes: attributes });
  }, 500);

  function handleCartFieldChange(event) {
    const field = event.target;

    if (field.matches('[data-cart-note]')) {
      persistNote(field.value);
      return;
    }

    const match = field.name && field.name.match(/^attributes\[(.+)\]$/);
    if (!match) return;
    if (field.type === 'radio' && !field.checked) return;

    const value = field.type === 'checkbox' ? (field.checked ? field.value : '') : field.value;
    persistAttribute(match[1], value);
  }

  document.addEventListener('change', handleCartFieldChange);
  document.addEventListener('input', handleCartFieldChange);
})();
